import React, {
  Suspense,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "../../api/axios";
import FetchContent from "../dynamic/fetchContent";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import FetchImage from "../dynamic/fetchImage";
import ImageContent from "../dynamic/ImageContent";
import Content from "../dynamic/Content";
import {
  EditButtonLarge,
  SaveButton,
  CancelButton,
} from "../buttons/Buttons";

const WEBSITE_URL = "/websitejson";
const JSON_KEY = "special-offers-links";
const ART = "en";

export const SpecialOffers = () => {
  const { username, auth } = useAuth();
  const { t } = useTranslation();

  const [editingId, setEditingId] = useState(0);
  const [tempLink, setTempLink] = useState("");
  const [links, setLinks] = useState([]);

  const linksRef = useRef(links);
  useEffect(() => {
    linksRef.current = links;
  }, [links]);

  const normalizeUrl = (link, description) => {
    if (!link) return "#";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailRegex.test(link.trim())) {
      const subject = encodeURIComponent("Special Offer Inquiry");

      const bodyText =
        `Hello,%0D%0A%0D%0A` +
        `I am interested in the following offer:%0D%0A%0D%0A` +
        `${encodeURIComponent(description || "")}%0D%0A%0D%0A` +
        `I need some details of this.%0D%0A%0D%0AThank you.`;

      return `mailto:${link.trim()}?subject=${subject}&body=${bodyText}`;
    }

    if (link.startsWith("http://") || link.startsWith("https://"))
      return link;

    return `https://${link.trim()}`;
  };

  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(WEBSITE_URL, {
        params: { username, art: ART, title: JSON_KEY },
      });

      if (response.data.entry) {
        const jsonData = JSON.parse(response.data.entry.content);

        if (jsonData !== "default text") {
          setLinks(
            jsonData.map((item) => ({
              ...item,
              description: item.description || "",
            }))
          );
        }
      }
    } catch (err) {
      console.error("Error fetching links:", err);
    }
  }, [username]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setTempLink(item.link);
  };

  const handleCancelClick = () => {
    setEditingId(0);
    setTempLink("");
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();

    const updated = links.map((l) =>
      l.id === editingId ? { ...l, link: tempLink } : l
    );

    setLinks(updated);
    setEditingId(0);
    setTempLink("");

    await axios.put(WEBSITE_URL, {
      username,
      art: ART,
      title: JSON_KEY,
      content: updated,
    });

    fetchLinks();
  };

  const deleteOffer = async (id) => {
    const updated = links.filter((l) => l.id !== id);
    setLinks(updated);

    await axios.put(WEBSITE_URL, {
      username,
      art: ART,
      title: JSON_KEY,
      content: updated,
    });
  };

  const addNewOffer = async () => {
    const newId = Date.now();

    const newOffer = {
      id: newId,
      title: `Card ${newId}`,
      link: "",
      description: "",
    };

    const updated = [...links, newOffer];
    setLinks(updated);

    await axios.put(WEBSITE_URL, {
      username,
      art: ART,
      title: JSON_KEY,
      content: updated,
    });
  };

  const renderClaimSection = (item) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {editingId === item.id && auth?.role ? (
          <>
            <input
              type="text"
              value={tempLink}
              onChange={(e) => setTempLink(e.target.value)}
              style={{
                width: "90%",
                padding: "6px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            <div>
              <SaveButton buttonText="Save" onClick={handleSaveClick} />
              <CancelButton buttonText="Cancel" onClick={handleCancelClick} />
            </div>
          </>
        ) : (
          <>
            <a
              href={normalizeUrl(item.link, item.description)}
              className="other-offers-span"
              target="_blank"
              rel="noopener noreferrer"
            >
              Claim Now
            </a>

            {auth?.role && (
              <>
                <div style={{ marginTop: "8px" }}>
                  <EditButtonLarge
                    buttonText={t("button.edit") || "Edit"}
                    onClick={() => handleEditClick(item)}
                  />
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => deleteOffer(item.id)}
                  style={{
                    marginTop: "5px",
                    background: "transparent",
                    border: "none",
                    color: "red",
                    cursor: "pointer",
                  }}
                >
                  Delete Offer
                </button>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="default">
      <div>
        <Suspense fallback={<h1>Still Loading…</h1>}>
          {!auth?.role ? (
            <FetchImage title="special-offers-main" type="image-about" />
          ) : (
            <ImageContent
              title="special-offers-main"
              username={username}
              type="image-about"
            />
          )}
        </Suspense>
      </div>

      <div className="contact-title">
        <h1>{t("special_offers_page.special-offers")}</h1>
      </div>

      <div className="container">
        {!auth?.role ? (
          <div className="p-brandnew">
            <FetchContent title="special-offers-p2" />
          </div>
        ) : (
          <div className="p-brandnew">
            <Content title={"special-offers-p2"} styling="p" />
          </div>
        )}
      </div>

      {auth?.role && (
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <button
            onClick={addNewOffer}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              cursor: "pointer",
              text: "black",
            }}
          >
            + Add Special Offer
          </button>
        </div>
      )}

      <div className="container other-services-container" style={{paddingBottom: "100px"}}>
        {/* {links.length === 0 && !auth?.role && (
          <p style={{ textAlign: "center", width: "100%" }}>
            No special offers available.
          </p>
        )} */}

        {links.map((item) => (
          <div className="other-services-item" key={item.id}>
            <div className="other-services-image">
              {!auth?.role ? (
                <FetchImage
                  title={`special-offers-card${item.id}`}
                  type="other-services-image"
                />
              ) : (
                <ImageContent
                  title={`special-offers-card${item.id}`}
                  username={username}
                  type="image-about"
                />
              )}

              {!auth?.role ? (
                <span className="title-span">
                  <FetchContent
                    title={`special-offers-card${item.id}`}
                    onTextLoaded={(text) => {
                      setLinks((prev) =>
                        prev.map((link) =>
                          link.id === item.id
                            ? { ...link, description: text }
                            : link
                        )
                      );
                    }}
                  />
                </span>
              ) : (
                <span className="title-span">
                  <Content
                    title={`special-offers-card${item.id}`}
                    styling="p"
                    onTextLoaded={(text) => {
                      setLinks((prev) =>
                        prev.map((link) =>
                          link.id === item.id
                            ? { ...link, description: text }
                            : link
                        )
                      );
                    }}
                  />
                </span>
              )}
            </div>

            {renderClaimSection(item)}
          </div>
        ))}
      </div>
    </div>
  );
};
